/**
 * @fileoverview Punto de entrada principal de la aplicación Nubelytics
 * 
 * Inicializa la aplicación React, monitores de diagnóstico, interceptores
 * y servicios necesarios siguiendo una arquitectura modular y orientada a componentes.
 * 
 * Incluye inicialización de monitores para microservicio de sincronización,
 * sistema de métricas para análisis de rendimiento e infraestructura CORS avanzada
 * para manejo de solicitudes cross-origin resiliente y automático.
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './index.css';
import App from './App';

// Inicializar monitor de API para diagnóstico de conectividad OAuth2
import apiMonitor from './utils/apiMonitor';
apiMonitor.initialize();

// Inicializar monitor del microservicio de sincronización
import syncMonitor from './monitoring/SyncServiceMonitor';
syncMonitor.startMonitoring({
  persistMetrics: true,
  onAlert: (alertData) => {
    console.warn('[SyncAlert]', alertData.message, alertData);
    // Integración con sistema de notificaciones existente
    if (alertData.type === 'availability' && window.notifyError) {
      window.notifyError('Problema en servicio de sincronización', alertData.message);
    }
  }
});

// Inicializar recolector de métricas
import metricsCollector from './monitoring/SyncMetricsCollector';
metricsCollector.startCollection();

// Inicializar infraestructura CORS avanzada con enfoque funcional
// La inicialización ocurre automáticamente al importar el módulo
import bootstrapCorsSolution from './infrastructure/cors/initCorsSolution';

// Inicializar de forma centralizada toda la solución CORS (incluye Axios & Fetch)
const corsRegistry = bootstrapCorsSolution();

// Log de diagnóstico para confirmar inicialización
if (process.env.NODE_ENV === 'development') {
  console.info('[Nubelytics] 🔄 Sistema CORS inicializado (registry):', corsRegistry);
}

// Inicializar interceptor global de API después de la infraestructura CORS
import './services/apiClient';

// Inicializar servicio centralizado de datos de usuario
import userDataService from './services/userDataService';

// Registrar observador de diagnóstico del servicio de usuario
if (process.env.NODE_ENV !== 'production') {
  userDataService.subscribe(userData => {
    console.debug('[UserDataService] Datos actualizados:', {
      authenticated: !!userData,
      uid: userData?.uid,
      storeId: userDataService.getStoreId(),
      tiendaSubdominio: userData?.tiendaSubdominio
    });
  });
}

// Eliminamos la lógica duplicada de Nexo
// import nexo from '@tiendanube/nexo';
// import { connect, iAmReady, ACTION_NAVIGATE_SYNC } from '@tiendanube/nexo';

// const nexoClient = nexo.create({ clientId: '18380', log: process.env.NODE_ENV !== 'production' });

// const bootstrap = async () => {
//   try {
//     await connect(nexoClient);
//     // Suscribimos la navegación antes de decir “ready”
//     nexoClient.subscribe(ACTION_NAVIGATE_SYNC, ({ path }) => {
//       window.location.hash = path;
//     });
//     iAmReady(nexoClient);
//   } catch (e) {
//     console.error('Handshake Nexo falló', e);
//   }
//   
//   const root = createRoot(document.getElementById('root'));
//   root.render(
//     <React.StrictMode>
//       <App nexo={nexoClient} />
//     </React.StrictMode>
//   );
// };

// bootstrap();

// Versión simplificada sin duplicar handshake (App se encarga)
const root = createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
